<template>
  <div class="admin-contacts">
    <div class="content-header">
      <div class="header-left">
        <h2>İletişim Mesajları</h2>
        <p>Sitenizden gelen tüm mesajları buradan yönetebilirsiniz</p>
      </div>
      <div class="header-right">
        <div class="filter-buttons">
          <button 
            @click="filterStatus = 'all'" 
            :class="{ active: filterStatus === 'all' }"
            class="filter-btn"
          >
            Tümü ({{ totalCount }})
          </button>
          <button 
            @click="filterStatus = 'unread'" 
            :class="{ active: filterStatus === 'unread' }"
            class="filter-btn"
          >
            Okunmamış ({{ unreadCount }})
          </button>
          <button 
            @click="filterStatus = 'read'" 
            :class="{ active: filterStatus === 'read' }"
            class="filter-btn"
          >
            Okunmuş ({{ readCount }})
          </button>
        </div>
        <button @click="refreshMessages" class="refresh-btn" :disabled="isLoading">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
          </svg>
          Yenile
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-state">
      <div class="loading-spinner"></div>
      <p>Mesajlar yükleniyor...</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="filteredMessages.length === 0" class="empty-state">
      <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor" opacity="0.3">
        <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
      </svg>
      <h3>{{ filterStatus === 'all' ? 'Henüz mesaj yok' : 'Bu filtreden mesaj yok' }}</h3>
      <p>{{ getEmptyStateMessage() }}</p>
    </div>

    <!-- Messages List -->
    <div v-else class="messages-container">
      <div class="messages-grid">
        <div 
          v-for="message in filteredMessages" 
          :key="message.id"
          :class="['message-card', { 'unread': !message.is_read }]"
        >
          <div class="message-header">
            <div class="sender-info">
              <div class="sender-avatar">
                {{ message.name.charAt(0).toUpperCase() }}
              </div>
              <div class="sender-details">
                <h4>{{ message.name }}</h4>
                <p class="sender-email">{{ message.email }}</p>
                <p v-if="message.phone" class="sender-phone">{{ message.phone }}</p>
              </div>
            </div>
            <div class="message-meta">
              <div class="message-date">{{ formatDate(message.created_at) }}</div>
              <div class="message-actions">
                <button 
                  @click="toggleReadStatus(message)"
                  :class="['read-btn', { 'read': message.is_read }]"
                  :title="message.is_read ? 'Okunmamış olarak işaretle' : 'Okunmuş olarak işaretle'"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                  </svg>
                </button>
                <button 
                  @click="deleteMessage(message)"
                  class="delete-btn"
                  title="Mesajı sil"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <div class="message-content">
            <div class="message-subject">
              <h5>{{ message.subject }}</h5>
            </div>
            <div class="message-body">
              {{ message.message }}
            </div>
          </div>

          <div v-if="!message.is_read" class="unread-indicator"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { adminApi } from '@/services/adminApi'

const emit = defineEmits(['statsUpdated'])

const messages = ref([])
const isLoading = ref(true)
const filterStatus = ref('all')

const filteredMessages = computed(() => {
  if (filterStatus.value === 'all') return messages.value
  if (filterStatus.value === 'unread') return messages.value.filter(m => !m.is_read)
  if (filterStatus.value === 'read') return messages.value.filter(m => m.is_read)
  return messages.value
})

const totalCount = computed(() => messages.value.length)
const unreadCount = computed(() => messages.value.filter(m => !m.is_read).length)
const readCount = computed(() => messages.value.filter(m => m.is_read).length)

const getEmptyStateMessage = () => {
  if (filterStatus.value === 'unread') return 'Tüm mesajlar okunmuş durumda'
  if (filterStatus.value === 'read') return 'Henüz okunmuş mesaj bulunmuyor'
  return 'İletişim formu üzerinden gelen mesajlar burada görünecek'
}

const loadMessages = async () => {
  try {
    isLoading.value = true
    const response = await adminApi.getContacts()
    if (response.success) {
      // Convert string values to proper types
      messages.value = (response.data || []).map(message => ({
        ...message,
        is_read: parseInt(message.is_read) === 1,
        id: parseInt(message.id)
      }))
      // Emit stats update to parent
      emit('statsUpdated', {
        total: messages.value.length,
        unread: messages.value.filter(m => !m.is_read).length
      })
    }
  } catch (error) {
    console.error('Mesajlar yüklenemedi:', error)
  } finally {
    isLoading.value = false
  }
}

const refreshMessages = () => {
  loadMessages()
}

const toggleReadStatus = async (message) => {
  try {
    if (!message.is_read) {
      const response = await adminApi.markContactAsRead(message.id)
      if (response.success) {
        message.is_read = true
        // Emit stats update to parent
        emit('statsUpdated', {
          total: messages.value.length,
          unread: messages.value.filter(m => !m.is_read).length
        })
      } else {
        alert('Okuma durumu güncellenemedi: ' + (response.error || 'Bilinmeyen hata'))
      }
    } else {
      const response = await adminApi.markContactAsUnread(message.id)
      if (response.success) {
        message.is_read = false
        // Emit stats update to parent
        emit('statsUpdated', {
          total: messages.value.length,
          unread: messages.value.filter(m => !m.is_read).length
        })
      } else {
        alert('Okuma durumu güncellenemedi: ' + (response.error || 'Bilinmeyen hata'))
      }
    }
  } catch (error) {
    console.error('Okuma durumu güncellenemedi:', error)
    alert('Okuma durumu güncellenemedi: ' + error.message)
  }
}

const deleteMessage = async (message) => {
  if (!confirm(`"${message.subject}" konulu mesajı silmek istediğinizden emin misiniz?`)) {
    return
  }

  try {
    const response = await adminApi.deleteContact(message.id)
    if (response.success) {
      messages.value = messages.value.filter(m => m.id !== message.id)
      // Emit stats update to parent
      emit('statsUpdated', {
        total: messages.value.length,
        unread: messages.value.filter(m => !m.is_read).length
      })
    } else {
      alert('Mesaj silinemedi: ' + (response.error || 'Bilinmeyen hata'))
    }
  } catch (error) {
    console.error('Mesaj silinemedi:', error)
    alert('Mesaj silinemedi: ' + error.message)
  }
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  const now = new Date()
  const diffTime = Math.abs(now - date)
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

  if (diffDays === 1) return 'Bugün'
  if (diffDays === 2) return 'Dün'
  if (diffDays < 7) return `${diffDays} gün önce`
  
  return date.toLocaleDateString('tr-TR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

onMounted(() => {
  loadMessages()
})
</script>

<style scoped>
.admin-contacts {
  padding: 0;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 24px;
}

.header-left h2 {
  margin: 0 0 8px 0;
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
}

.header-left p {
  margin: 0;
  color: #64748b;
  font-size: 16px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.filter-buttons {
  display: flex;
  background: #f1f5f9;
  border-radius: 8px;
  padding: 4px;
  gap: 2px;
}

.filter-btn {
  padding: 8px 16px;
  border: none;
  background: transparent;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.filter-btn:hover {
  color: #3b82f6;
}

.filter-btn.active {
  background: white;
  color: #3b82f6;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.refresh-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.refresh-btn:hover:not(:disabled) {
  background: #2563eb;
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.loading-state, .empty-state {
  text-align: center;
  padding: 64px 32px;
  color: #64748b;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f1f5f9;
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state h3 {
  margin: 16px 0 8px 0;
  font-size: 18px;
  color: #374151;
}

.messages-container {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.messages-grid {
  display: flex;
  flex-direction: column;
}

.message-card {
  position: relative;
  padding: 24px;
  border-bottom: 1px solid #e2e8f0;
  transition: all 0.2s ease;
}

.message-card:last-child {
  border-bottom: none;
}

.message-card:hover {
  background: #f8fafc;
}

.message-card.unread {
  background: #eff6ff;
  border-left: 4px solid #3b82f6;
}

.message-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.sender-info {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.sender-avatar {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  color: white;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  flex-shrink: 0;
}

.sender-details h4 {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}

.sender-email, .sender-phone {
  margin: 2px 0;
  font-size: 14px;
  color: #64748b;
}

.message-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.message-date {
  font-size: 13px;
  color: #94a3b8;
  font-weight: 500;
}

.message-actions {
  display: flex;
  gap: 8px;
}

.read-btn, .delete-btn {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.read-btn {
  background: #f1f5f9;
  color: #64748b;
}

.read-btn:hover {
  background: #e2e8f0;
  color: #3b82f6;
}

.read-btn.read {
  background: #dcfce7;
  color: #16a34a;
}

.delete-btn {
  background: #fef2f2;
  color: #dc2626;
}

.delete-btn:hover {
  background: #fecaca;
}

.message-content {
  margin-left: 60px;
}

.message-subject h5 {
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}

.message-body {
  font-size: 15px;
  line-height: 1.6;
  color: #374151;
  white-space: pre-line;
  max-height: 120px;
  overflow-y: auto;
}

.unread-indicator {
  position: absolute;
  top: 24px;
  right: 24px;
  width: 8px;
  height: 8px;
  background: #3b82f6;
  border-radius: 50%;
}

@media (max-width: 768px) {
  .content-header {
    flex-direction: column;
    align-items: stretch;
  }
  
  .header-right {
    justify-content: space-between;
  }
  
  .filter-buttons {
    flex: 1;
    justify-content: center;
  }
  
  .message-header {
    flex-direction: column;
    gap: 12px;
  }
  
  .message-meta {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
  
  .message-content {
    margin-left: 0;
    margin-top: 12px;
  }
}
</style>